import { Component, Input, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { DatabaseService } from 'src/app/servicios/database/database.service';
import { MessageService } from 'src/app/servicios/message/message.service';
import { PermissionsService } from 'src/app/servicios/permissions/permissions.service';
import { FsessionService } from 'src/app/servicios/session/fsession.service';
import { ValidRoles } from 'src/app/utils/directives/validateRoles/validate-roles.directive';

import { Ayuda } from '../../models/ayuda.model';
import {
  preloadHelpPermissions,
} from '../../utils/help-permissions.util';
import { CrearModificarAyudaComponent } from '../ayudas-html/crear-modificar-ayuda/crear-modificar-ayuda.component';
import { DialogoLeerAyudasComponent } from './dialogo-leer-ayudas/dialogo-leer-ayudas.component';

@Component({
  selector: 'app-consultar-ayuda',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    SweetAlert2Module,
    TranslateModule,
    MatStepperModule,
  ],
  templateUrl: './consultar-ayuda.component.html',
  styleUrl: './consultar-ayuda.component.scss',
})
export class ConsultarAyudaComponent implements OnInit {

  private dialogService = inject(MatDialog);
  private permissionService = inject(PermissionsService);
  private router = inject(Router);
  private database = inject(DatabaseService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private snackBar = inject(MatSnackBar);
  private fsession = inject(FsessionService);

  /** Submódulo opcional cuando el botón no se usa desde una ruta gestionada por el guard */
  @Input() submodulo: number | string | null = null;

  /** Permite cambiar el ícono del botón sin recompilar (default: "help") */
  @Input() icon = 'help';

  private translations: Record<string, string> = {};

  ngOnInit(): void {
    this.translateService
      .get([
        'submodulo-sin-ayudas',
        'submodulo-ayuda-no-disponible',
        'help-crear-confirmacion',
        'aceptar',
        'cancelar',
        'haOcurridoUnError',
      ])
      .subscribe((t) => (this.translations = t));
  }

  private isSuperAdmin(): boolean {
    return Number(this.fsession.optenerValor('dato40')) === ValidRoles.SUPER_ADMINISTRADOR;
  }

  /**
   * Resuelve el id de submódulo: @Input, PermissionsService o `data.permission` de la ruta activa.
   */
  private resolveSubmoduloId(): number | string | null {
    if (this.submodulo != null && this.submodulo !== '') {
      return this.submodulo;
    }

    const active = this.permissionService.getActiveRouteId();
    if (active != null && active !== '' && Number(active) > 0) {
      return active;
    }

    let route = this.router.routerState.snapshot.root;
    let permissionFromRoute: number | null = null;
    while (route) {
      const raw = route.data?.['permission'];
      if (raw != null && raw !== '' && !Number.isNaN(Number(raw))) {
        const n = Number(raw);
        if (n > 0) {
          permissionFromRoute = n;
        }
      }
      route = route.firstChild;
    }

    return permissionFromRoute;
  }

  /**
   * Carga las ayudas del submódulo activo y abre el diálogo lector.
   * Si no hay ayudas, muestra un aviso; super admin puede crear una nueva.
   */
  consultarAyuda(): void {
    const ids = this.resolveSubmoduloId();
    if (ids == null || ids === '' || Number(ids) <= 0) {
      this.messageService.SwalMessage(
        'info',
        this.translations['submodulo-ayuda-no-disponible']
          ?? 'No hay un submódulo activo. Abra una pantalla del menú para consultar su ayuda.',
        false,
        3000,
      );
      return;
    }

    const loadingMsg = this.translateService.instant('cargando-ayudas-pantalla');
    const snack: MatSnackBarRef<TextOnlySnackBar> = this.snackBar.open(loadingMsg, 'Ok', {
      horizontalPosition: 'left',
      verticalPosition: 'bottom',
    });

    preloadHelpPermissions(this.database, this.permissionService).subscribe({
      next: () => {
        this.database.consultarAyudasSubmodulo(ids).subscribe({
          next: (ayudas) => {
            snack.dismiss();

            const lista = Object.values(ayudas) as Ayuda[];

            if (lista.length === 0) {
              void this.handleSinAyudas(ids);
              return;
            }

            this.dialogService.open(DialogoLeerAyudasComponent, {
              width: '75%',
              maxWidth: '100vw',
              height: '80%',
              autoFocus: false,
              panelClass: ['ayuda-read-dialog', 'unset-mat-content-height', 'mat-dialog-container-stock'],
              data: { pages: lista, length: lista.length },
            });
          },
          error: () => {
            snack.dismiss();
            this.messageService.SwalMessage('error', this.translations['haOcurridoUnError'] ?? 'Error');
          },
        });
      },
      error: () => {
        snack.dismiss();
        this.messageService.SwalMessage('error', this.translations['haOcurridoUnError'] ?? 'Error');
      },
    });
  }

  /** Aviso sin ayudas; super admin puede abrir el diálogo de creación. */
  private async handleSinAyudas(idSubmodulo: number | string): Promise<void> {
    await this.messageService.SwalMessage(
      'info',
      this.translations['submodulo-sin-ayudas'],
      false,
      2000,
    );

    if (!this.isSuperAdmin()) {
      return;
    }

    const texts = await firstValueFrom(
      this.translateService.get(['help-crear-confirmacion', 'aceptar', 'cancelar']),
    );

    const result = await this.messageService.dialogMessage(
      'question',
      texts['help-crear-confirmacion']
        ?? '¿Desea crear una ayuda para este submódulo?',
      {
        showCancelButton: true,
        cancelButtonText: texts['cancelar'] ?? 'Cancelar',
        confirmButtonText: texts['aceptar'] ?? 'Sí',
      },
    );

    if (result.isConfirmed) {
      this.openCrearAyudaDialog(Number(idSubmodulo));
    }
  }

  private openCrearAyudaDialog(idSubmodulo: number): void {
    this.dialogService.open(CrearModificarAyudaComponent, {
      width: '65%',
      height: '85%',
      maxWidth: '100vw',
      panelClass: 'ayuda-crud-dialog',
      autoFocus: false,
      restoreFocus: false,
      data: { IdSubmodulo: idSubmodulo },
    });
  }
}
